import { useState, useEffect, useRef, useCallback } from "react";

const INACTIVITY_TIME = 15 * 60 * 1000; // 15 minutos (antes estaba comentado como 5 pero el valor era 15)
const WARNING_TIME = 30 * 1000; // 30 segundos
const STORAGE_KEY = "lastActivity";

const useInactivityDetector = (onLogout, isActive) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [remainingTime, setRemainingTime] = useState(WARNING_TIME / 1000);

  const checkIntervalRef = useRef(null);

  // Función para actualizar la marca de tiempo de actividad
  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, now.toString());
  }, []);

  const resetTimer = useCallback(() => {
    setIsModalVisible(false);
    setRemainingTime(WARNING_TIME / 1000);
    updateLastActivity();
  }, [updateLastActivity]);

  useEffect(() => {
    if (!isActive) {
      setIsModalVisible(false);
      return;
    }

    // Inicializar marca de tiempo si no existe
    if (!localStorage.getItem(STORAGE_KEY)) {
      updateLastActivity();
    }

    const checkStatus = () => {
      const lastActivity = parseInt(localStorage.getItem(STORAGE_KEY) || Date.now(), 10);
      const diff = Date.now() - lastActivity;

      if (diff >= INACTIVITY_TIME) {
        // Cierre de sesión definitivo
        setIsModalVisible(false);
        clearInterval(checkIntervalRef.current);
        onLogout();
      } else if (diff >= INACTIVITY_TIME - WARNING_TIME) {
        // Mostrar advertencia
        setIsModalVisible(true);
        const timeLeft = Math.ceil((INACTIVITY_TIME - diff) / 1000);
        setRemainingTime(timeLeft > 0 ? timeLeft : 0);
      } else {
        // Usuario activo
        if (isModalVisible) setIsModalVisible(false);
      }
    };

    // Escuchar eventos de usuario locales
    const events = ["mousemove", "keydown", "click", "scroll"];
    const handleActivity = () => updateLastActivity();

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Sincronización multi-pestaña: escuchar cambios en localStorage de otras pestañas
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        // Si otra pestaña se activó, ocultar modal aquí si estaba visible
        if (isModalVisible) setIsModalVisible(false);
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Intervalo de chequeo constante y simplificado
    checkIntervalRef.current = setInterval(checkStatus, 1000);

    return () => {
      clearInterval(checkIntervalRef.current);
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isActive, onLogout, updateLastActivity, isModalVisible]);

  return { isModalVisible, remainingTime, resetTimer };
};

export default useInactivityDetector;
